import React, { ReactNode, useCallback, useRef } from 'react';
import { mergeProps, useButton, useHover } from 'react-aria';
import { useControlledState } from '@react-stately/utils';
import { genericMemo } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { Header, type HeaderLevel, type HeaderProps } from '../Header';
import { HStack, Stack, VStack } from '../Stack';
import { HelpProps } from '../Help';
import { IconArrowDown, IconArrowRight } from '../../icons';
import { headerSkinSizes } from '../Header/utils';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { TestProps } from '../../internal/types/testTypes';
import { useTheme } from '../../lib/theme';
import './Accordion.css';

type AccordionLevel = Exclude<HeaderLevel, 'h1'>;

export interface AccordionProps extends TestProps, Omit<HeaderProps, 'level' | 'addonLeft' | 'title'> {
    level: AccordionLevel;
    /**
     * Тип аккордеона.
     * 'left' - стрелка слева, 'right' - стрелка справа.
     * @default 'left'
     *
     */
    type?: 'left' | 'right';
    /**
     * Раскрыта ли строка.
     * @default false
     */
    isExpanded?: boolean;
    onExpandToggle?: (isExpanded: boolean) => void;
    /**
     * Аддон слева от заголовка.
     */
    addonLeft?: React.ReactNode;
    /**
     * Заголовок аккордеона.
     */
    title?: string | React.ReactNode;
    /**
     * Содержимое аккордеона (отображается, когда Accordion в раскрытом состоянии).
     */
    children?: ReactNode;
}

const cls = cn('Accordion');
const sizeSkinLayoutClass = getSizeSkin('layout');

/**
 * Компонент для создания заголовков уровней h2-h5 со стрелкой, отображающей открытое / закрытое состояние
 */
export const Accordion = genericMemo(function Accordion(props: AccordionProps) {
    const {
        'data-testid': dataTestId = 'Accordion',
        type = 'left',
        level,
        className,
        addonLeft,
        onExpandToggle,
        title,
        children,
        isExpanded: isExpandedFromProps,
        help: helpRaw,
        ...headerProps
    } = props;
    const onToggleHandler = useCallback(
        (isExpanded: boolean) => {
            onExpandToggle?.(isExpanded);
        },
        [onExpandToggle],
    );
    const [isExpanded, setExpanded] = useControlledState(isExpandedFromProps, false, onToggleHandler);
    const handleClick = useCallback(() => {
        setExpanded(!isExpanded);
    }, [isExpanded, setExpanded]);

    const ref = useRef<HTMLDivElement>(null);
    const { buttonProps } = useButton({ onPress: handleClick, elementType: 'div' }, ref);
    const { hoverProps, isHovered } = useHover({});

    const skinSize = headerSkinSizes[level];
    const { iconSize } = useTheme().sizeTokens[skinSize];
    const sizeSkinClass = getSizeSkin(skinSize);
    const colorSkinClass = getColorSkin('gray-transparent', {});

    const help = useCallback((helpProps: HelpProps) => helpRaw?.({ ...helpProps, as: 'span' }), [helpRaw]);

    const Arrow = type === 'left' ? IconArrowRight : IconArrowDown;
    const arrow = (
        <Arrow
            className={cls(
                'expander',
                {
                    opened: isExpanded,
                },
                [sizeSkinClass],
            )}
            data-testid={`${dataTestId}.ExpandIcon`}
            data-expanded={isExpanded}
            role="supplementary"
            color="current"
            size={iconSize}
        />
    );
    return (
        <VStack
            data-testid={`${dataTestId}.Wrapper`}
            className={cls(
                '',
                {
                    type,
                    opened: isExpanded,
                    hovered: isHovered,
                },
                [className, colorSkinClass, sizeSkinLayoutClass],
            )}
        >
            <div
                {...mergeProps(buttonProps, hoverProps)}
                className={cls('trigger')}
                data-testid={dataTestId}
                ref={ref}
                role="button"
            >
                <HStack className={cls('triggerContent', [sizeSkinClass])}>
                    {type === 'left' ? arrow : null}
                    {addonLeft ? <Stack className={cls('addonLeft')}>{addonLeft}</Stack> : null}
                    <Header
                        data-testid={`${dataTestId}.Header`}
                        className={cls('header')}
                        level={level}
                        help={help}
                        {...headerProps}
                    >
                        {title}
                    </Header>
                    {type === 'right' ? arrow : null}
                </HStack>
            </div>
            {isExpanded && children ? <div data-testid={`${dataTestId}.Panel`}>{children}</div> : null}
        </VStack>
    );
});
